<script setup>
/**
 * RM-400 (Kapil, 10/13) — the inquiry's optional details, on their own screen after
 * the budget: occasion, room preference, seating preference, special requests. All
 * optional — the restaurant's events team settles what is available — so nothing
 * here blocks Review & Submit, and a chip taps on and off. Room and seating take
 * every type that works (10/21); the "is the request essential?" answer is gone.
 */
import moment from 'moment-timezone'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useReservationsStore } from '@/stores'
import { joinRoomPreferences, roomPreferencesOf } from '@/utils/inquiryPreferences'

const props = defineProps({
  selectedRestaurantNames: { type: String, default: '' },
  selectedRestaurantsNotAcceptingReferrals: { type: Array, default: null },
  roomPreferenceOptions: { type: Array, default: () => [] },
  seatingPreferenceOptions: { type: Array, default: () => [] },
  saving: { type: Boolean, default: false },
})

const emit = defineEmits(['next', 'change'])

const reservationsStore = useReservationsStore()
const { editingReservation } = storeToRefs(reservationsStore)

const OCCASIONS = ['Anniversary', 'Birthday', 'Business', 'Date', 'General', 'Special Occasion']

// Room is multiple choice too (the product agent and Kapil, 10/21): every room type that
// works - Private, Semi-Private, Main Dining - stored as the joined string ("Private,
// Semi-Private"). An older inquiry's value ("Private Room Only") opens as the chips it named.
const roomSelected = computed(() => roomPreferencesOf(editingReservation.value?.roomPreference))
const toggleRoom = value => {
  const current = roomSelected.value
  const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value]
  editingReservation.value.roomPreference = joinRoomPreferences(props.roomPreferenceOptions.filter(o => next.includes(o)))
  markChanged()
}

// Seating is multiple choice (Kapil, 10/14): every type that works, stored as the
// joined string every display and email prints ("One Large Table, Multiple Round").
const SEATING_SEPARATOR = ', '
const seatingSelected = computed(() => String(editingReservation.value?.seatingPreference || '').split(SEATING_SEPARATOR).map(v => v.trim()).filter(Boolean))
const toggleSeating = value => {
  const current = seatingSelected.value
  const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value]
  const ordered = props.seatingPreferenceOptions.filter(o => next.includes(o))
  editingReservation.value.seatingPreference = ordered.length ? ordered.join(SEATING_SEPARATOR) : null
  markChanged()
}

const markChanged = () => emit('change')
const toggle = (field, value) => {
  editingReservation.value[field] = editingReservation.value[field] === value ? null : value
  markChanged()
}
</script>

<template>
  <div class="tab-content occasion-step">
    <div class="occasion-context">
      <div class="occasion-context-name">
        {{ selectedRestaurantNames }}
      </div>
      <div class="occasion-context-details">
        {{ editingReservation.date ? moment(editingReservation.date).format('ddd, MMM D') : '' }}
        <template v-if="editingReservation.preferredTime">
          · {{ editingReservation.preferredTime }}
        </template>
        <template v-if="editingReservation.partySize">
          · {{ editingReservation.partySize }} {{ editingReservation.partySize == 1 ? 'guest' : 'guests' }}
        </template>
      </div>
    </div>

    <div class="optional-head">
      <div class="optional-title">
        {{ $t('reservation.private.optionalDetailsTitle') }}
      </div>
      <div class="optional-lead">
        {{ $t('reservation.private.optionalDetailsLead') }}
      </div>
    </div>

    <div class="occasion-chips-section">
      <div class="occasion-chips-label">
        Occasion
      </div>
      <div class="occasion-chips-grid">
        <button
          v-for="occ in OCCASIONS"
          :key="occ"
          type="button"
          class="occasion-chip"
          :class="{ 'occasion-chip--active': editingReservation.occasion === occ }"
          @click="toggle('occasion', occ)"
        >
          {{ occ }}
        </button>
      </div>
    </div>

    <div class="occasion-chips-section">
      <div class="occasion-chips-label">
        Room Preference
      </div>
      <div class="occasion-chips-grid">
        <button
          v-for="room in roomPreferenceOptions"
          :key="room"
          type="button"
          class="occasion-chip"
          :class="{ 'occasion-chip--active': roomSelected.includes(room) }"
          @click="toggleRoom(room)"
        >
          {{ room }}
        </button>
      </div>
    </div>

    <div class="occasion-chips-section">
      <div class="occasion-chips-label">
        Seating Preference
      </div>
      <div class="occasion-chips-grid">
        <button
          v-for="seat in seatingPreferenceOptions"
          :key="seat"
          type="button"
          class="occasion-chip"
          :class="{ 'occasion-chip--active': seatingSelected.includes(seat) }"
          @click="toggleSeating(seat)"
        >
          {{ seat }}
        </button>
      </div>
    </div>

    <div class="occasion-chips-section occasion-notes-field">
      <!--
        The label sits above the box like the other sections (Kapil, 10/21: room to read); the example is the
        placeholder. "Is special request essential?" is gone: an inquiry is not a guaranteed booking.
      -->
      <div class="occasion-chips-label">
        Notes / Requests
      </div>
      <VTextarea
        v-model="editingReservation.specialRequests"
        placeholder="e.g. outdoor only, need A/V, no pre-set menu preferred"
        rows="3"
        auto-grow
        variant="outlined"
        hide-details
        class="special-requests-field"
        @update:model-value="markChanged"
      />
    </div>

    <div class="text-center mt-8 mb-2">
      <VBtn
        :disabled="!!selectedRestaurantsNotAcceptingReferrals || saving"
        color="primary-dark"
        size="large"
        block
        class="occasion-next-btn"
        @click="emit('next')"
      >
        Review & Submit
      </VBtn>
      <div
        v-if="selectedRestaurantsNotAcceptingReferrals"
        class="mt-2 text-caption"
      >
        {{ $t('reservation.preferred.removeToProceeed') }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$text-dark: var(--ds-text-dark);
$text-medium: var(--ds-text-medium);

.tab-content {
  position: relative;
}

.occasion-step {
  padding: 0 20px 8px;
  max-width: 420px;
  margin: 0 auto;
}

.occasion-context {
  text-align: center;
  padding: 16px 0 8px;
}

.occasion-context-name {
  font-size: 18px;
  font-weight: 700;
  color: $text-dark;
  line-height: 1.3;
}

.occasion-context-details {
  font-size: 13px;
  color: $text-medium;
  margin-top: 2px;
}

.optional-head {
  padding: 12px 0 4px;
}

.optional-title {
  font-size: 16px;
  font-weight: 700;
  color: $text-dark;
}

.optional-lead {
  font-size: 14px;
  color: $text-medium;
  margin-top: 2px;
}

.optional-sub {
  font-weight: 400;
  color: $text-medium;
}

.occasion-chips-section {
  padding: 12px 0 4px;
}

.occasion-chips-label {
  font-size: 14px;
  font-weight: 600;
  color: $text-dark;
  margin-bottom: 10px;
}

.occasion-chips-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.occasion-chip {
  padding: 8px 16px;
  border-radius: 20px;
  border: 1.5px solid #e0e0e0;
  background: #fff;
  font-size: 14px;
  font-weight: 500;
  color: $text-dark;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;

  &:hover {
    border-color: #bbb;
  }

  &--active {
    border-color: var(--ds-brand-primary-dark);
    background: var(--ds-brand-primary-light);
    color: var(--ds-brand-primary-dark);
    font-weight: 600;
  }
}

.occasion-notes-field {
  padding-top: 16px;
}

// Room to read and write (Kapil, 10/21): an outlined box in the chips' border, 16px inside, text on its own.
.special-requests-field :deep(.v-field__outline) {
  --v-field-border-width: 1.5px;
  --v-field-border-opacity: 1;
  color: #e0e0e0;
}

// The visible textarea and the hidden sizer auto-grow measures with share one metric, so the box
// keeps growing with the request (three lines to start, from rows="3").
.special-requests-field :deep(.v-field__input),
.special-requests-field :deep(.v-textarea__sizer) {
  --v-input-padding-top: 16px;
  --v-field-padding-bottom: 16px;
  padding-inline: 16px;
  font-size: 16px;
  line-height: 1.5;
}

.occasion-next-btn {
  max-width: 340px;
  margin: 0 auto;
  font-weight: 600;
  letter-spacing: 0.02em;
}
</style>
